import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, HostListener, ChangeDetectorRef, OnChanges, SimpleChanges, signal, computed, effect, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription, interval, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { LayoutService } from '../../core/services/layout/layout.service';
import { NavigationService, NavigationItem, NavigationSubItem } from '../../core/services/sidebar/sidebar.service';
import { SubmenuCountService } from '../../core/services/sidebar/submenu-count.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ProjectHoursService } from '@app/core/services/sidebar/project-hours.service';
import { LogTimeService } from '@app/pages/projects/projects.service';
import * as Highcharts from 'highcharts';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { SidebarFestivalDecoration } from '@app/shared/components/sidebar-festival-decoration/sidebar-festival-decoration';


interface WidgetItem {
  iconClass: string;
  label: string;
  count?: number;
  link?: string;
  type?: 'default' | 'warning' | 'danger';
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatTooltipModule, Spinner, SidebarFestivalDecoration],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() isCollapsed = false;
  @Input() isChangePasswordOpen = false;
  @Output() toggleCollapse = new EventEmitter<void>();
  @Input() userInfoBg: string = 'assets/images/theme-banner.png';
  userName: string | null = null;
  isTransitioning = false;
  greeting = 'Good Morning';
  currentTimePeriod: 'morning' | 'afternoon' | 'evening' | 'night' = 'morning';
  needsTextOverlay = false;
  navigationItems: NavigationItem[] = [];
  private subscription = new Subscription();
  private routerSubscription: Subscription;
  private destroy$ = new Subject<void>();
  activeHeader: NavigationItem | null = null;
  activeSubItem: NavigationSubItem | null = null;
  isRadialMenuOpen = false;
  isDashboard = false;
  isMainPage = false; // For header navigation highlighting
  openWidgetDropdown: number | null = null;
  openSectionDropdown: string | null = null;

  // Time Summary properties
  currentItemId = signal<string | null>(null);
  currentItemType = signal<'task' | 'revision' | null>(null);
  taskHourStatus = signal<any>(null);
  taskHourData = signal<any>(null);
  taskPercentage = signal<number>(0);
  isTimeSummaryLoading = signal<boolean>(false);
  isChartsLoading = signal<boolean>(false);
  showTimeSummary = signal<boolean>(false);
  
  // Chart instances and containers
  @ViewChild('taskStatusChartContainer') taskStatusChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('balanceHoursChartContainer') balanceHoursChartContainer!: ElementRef<HTMLDivElement>;
  private chartInstances: any[] = [];
  private loadingTimeout: any = null;
  private lastProcessedDataHash: string | null = null;
  private isProcessingCacheUpdate = false;

  // Radial menu properties
  radialMenuItems = [
    {
      id: 'timesheets',
      label: 'Timesheets',
      icon: 'wl_icon_timesheet-icon',
      route: '/timesheets'
    },
    {
      id: 'projects-all',
      label: 'Projects',
      icon: 'icon-all_projects',
      route: '/projects/projects-all'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'icon-projects_i_own',
      route: '/dashboard'
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: 'icon-task',
      route: '/requests'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: 'wl_icon_clients',
      route: '/clients-list'
    }
  ];

  statusItems = [
    { label: 'Working on', count: 0 },
    { label: 'Pending', count: 0 },
    { label: 'Approvals', count: 0 }
  ];

  widgets: WidgetItem[] = [
    {
      iconClass: 'wl_icon_mytask',
      label: 'My Tasks',
      count: 25
    },
    {
      iconClass: 'wl_icon_myapprovals',
      label: 'My Approvals',
      count: 10
    },
    {
      iconClass: 'wl_icon_pending',
      label: 'Pending',
      count: 18,
      type: 'danger'
    },
    {
      iconClass: 'wl_icon_qaissues',
      label: 'QA Issues',
      count: 2,
      type: 'warning'
    },
    {
      iconClass: 'wl_icon_timesheet-icon',
      label: 'My Timesheet'
    },
    {
      iconClass: 'wl_icon_workingon',
      label: 'Working On',
      count: 18
    },
    {
      iconClass: 'wl_icon_request',
      label: 'Requests',
      count: 18
    },
    {
      iconClass: 'wl_icon_queues',
      label: 'Queues',
      count: 3
    },
    {
      iconClass: 'wl_icon_whosoff',
      label: 'Who\'s Off',
      count: 2,
      type: 'warning'
    }
  ];

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private navigationService: NavigationService,
    private submenuCountService: SubmenuCountService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private projectHoursService: ProjectHoursService,
    private logTimeService: LogTimeService
  ) {
    // Subscribe to navigation config once in constructor
    this.subscription.add(
      this.navigationService.getNavigationConfig().subscribe((data: any) => {
        this.navigationItems = data;
      })
    );

    this.routerSubscription = this.router.events.subscribe((event) => {
      this.isDashboard = this.router.url === '/' || this.router.url === '/dashboard';
      this.isMainPage = this.router.url === '/' || this.router.url === '/dashboard' || this.router.url === '/projects' || this.router.url.startsWith('/projects/');
    });

    // Watch for percentageComplete changes from ProjectHoursService
    // This will reactively update the chart when status changes in status-sidebar
    effect(() => {
      const projectHours = this.projectHoursService.getCurrentProjectHours()();
      const itemId = this.currentItemId();
      const itemType = this.currentItemType();
      
      // Only update if we have matching item and percentageComplete is available
      if (projectHours && itemId && itemType) {
        const objCode = itemType.toUpperCase() as 'TASK' | 'REVISION';
        const isMatch = 
          (objCode === 'TASK' && projectHours.taskID === itemId) ||
          (objCode === 'REVISION' && projectHours.revisionID === itemId);
        
        if (isMatch && projectHours.percentageComplete !== undefined && projectHours.percentageComplete !== null) {
          // Update task percentage with the new value from API
          const newPercentage = Math.round(projectHours.percentageComplete);
          const currentPercentage = this.taskPercentage();
          
          // Only update if the value has changed to avoid unnecessary chart re-initialization
          if (newPercentage !== currentPercentage) {
            this.taskPercentage.set(newPercentage);
            
            // Re-initialize the chart if time summary is visible and chart container is available
            if (this.showTimeSummary() && this.taskHourStatus()) {
              setTimeout(() => {
                this.forceChartInitialization();
              }, 100);
            }
          }
        }
      }
    });
  }

  toggleSidebar() {
    // Add transitioning class to prevent overflow issues during animation
    this.isTransitioning = true;
    
    this.layoutService.toggleSidebar();
    
    // Remove transitioning class after animation completes (250ms)
    setTimeout(() => {
      this.isTransitioning = false;
      this.cdr.detectChanges();
    }, 250);
  }

  ngOnInit(): void {
    this.userName = localStorage.getItem('userName') || 'User';
    this.setGreetingAndBg();
    this.getWorkingCount();

    // Subscribe to navigation changes
    this.subscription.add(
      this.navigationService.getActiveHeader().subscribe(header => {
        // console.log('=== SIDEBAR - Active Header Changed ===');
        // console.log('New active header:', header);
        this.activeHeader = header;
        // Auto-open submenu if header is active and has sub-items
        if (header && this.hasChildren(header)) {
          // console.log('Auto-opening submenu for:', header.to);
          this.openSubmenus.add(header.to);
        }
      })
    );

    this.subscription.add(
      this.navigationService.getActiveSubItem().subscribe(subItem => {
        this.activeSubItem = subItem;
        // Auto-open parent submenu when sub-item is active
        if (subItem) {
          const parentHeader = this.navigationItems.find(item =>
            item.subs && item.subs.some(sub => sub.to === subItem.to)
          );
          if (parentHeader) {
            this.openSubmenus.add(parentHeader.to);
          }
        }
      })
    );

      // Subscribe to route changes to auto-open current page submenu and update menu states
      this.subscription.add(
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            const currentSubmenu = this.getCurrentPageSubmenu();
            if (currentSubmenu && this.hasChildren(currentSubmenu)) {
              this.openSubmenus.add(currentSubmenu.to);
            }
            
            // Debug logging for detail pages
            const detailPageInfo = this.getCurrentDetailPageInfo();
            if (detailPageInfo) {
              console.log('🔍 Sidebar: Detected detail page:', detailPageInfo);
              console.log('🔍 Sidebar: Current submenu:', currentSubmenu);
              console.log('🔍 Sidebar: Navigation items:', this.navigationItems);
            }
            
            // Check and load time summary for task detail pages
            this.checkAndLoadTimeSummary();
            
            // Force change detection to update menu states
            setTimeout(() => {
              this.cdr.detectChanges();
              // Force refresh menu state after route change
              this.forceRefreshMenuState();
            }, 0);
          }
        })
      );

    // Initial check for dashboard
    this.isDashboard = this.router.url === '/' || this.router.url === '/dashboard';
    this.isMainPage = this.router.url === '/' || this.router.url === '/dashboard' || this.router.url === '/projects' || this.router.url.startsWith('/projects/');
    
    // Check for task detail page and load time summary
    this.checkAndLoadTimeSummary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInfoBg'] && !changes['userInfoBg'].firstChange) {
      // When userInfoBg input changes, trigger change detection
      this.cdr.detectChanges();

      // If theme was disabled, refresh to show correct time-based background
      if (!this.isThemeActive()) {
        this.setGreetingAndBg();
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Set greeting and userInfoBg based on time of day, using random background from assets/images/left_sidebar/greeting_bg
   * Only sets background if no theme is active from layout
   */
  private setGreetingAndBg() {
    const hour = new Date().getHours();
    let period = 'morning';
    if (hour >= 5 && hour < 12) {
      this.greeting = 'Good Morning';
      period = 'morning';
      this.needsTextOverlay = true; // Bright daylight hours need overlay
    } else if (hour >= 12 && hour < 17) {
      this.greeting = 'Good Afternoon';
      period = 'afternoon';
      this.needsTextOverlay = true; // Bright daylight hours need overlay
    } else if (hour >= 17 && hour < 20) {
      this.greeting = 'Good Evening';
      period = 'evening';
      this.needsTextOverlay = false; // Dusk hours, less bright
    } else {
      this.greeting = 'Good Evening';
      period = 'night';
      this.needsTextOverlay = false; // Dark hours, no overlay needed
    }
    this.currentTimePeriod = period as 'morning' | 'afternoon' | 'evening' | 'night';

    // Only set background if no theme is active OR if userInfoBg is from the layout's time-based system
    if (!this.isThemeActive() || this.isLayoutTimeBasedBackground()) {
      // List of available images for each period
      const bgImages: { [key: string]: string[] } = {
        morning: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_morning_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_morning_2.jpg',
        ],
        afternoon: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_2.png',
        ],
        evening: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_evening_1.png',
          'assets/images/left_sidebar/greeting_bg/greet_bg_evening_2.png',
        ],
        night: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_night_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_night_2.jpg',
        ],
      };
      const imgs = bgImages[period] || bgImages['morning'];
      this.userInfoBg = imgs[Math.floor(Math.random() * imgs.length)];
    }
  }

  private isThemeActive(): boolean {
    return localStorage.getItem('theme-background-active') === 'true';
  }

  private isLayoutTimeBasedBackground(): boolean {
    // Check if the current userInfoBg is from layout's time-based system
    const layoutTimeBasedBgs = [
      'assets/images/theme-banner.png',
      'assets/images/theme-banner-ocean.png',
      'assets/images/theme-banner-dark.png'
    ];
    return layoutTimeBasedBgs.includes(this.userInfoBg);
  }

  onAddClick() {
    // Toggle radial menu
    this.isRadialMenuOpen = !this.isRadialMenuOpen;
  }

  onRadialMenuItemClick(item: any, event: Event) {
    event.stopPropagation();
    this.isRadialMenuOpen = false;

    if (item.queryParams) {
      this.router.navigate([item.route], { queryParams: item.queryParams });
    } else {
      this.router.navigate([item.route]);
    }
  }

  public toggleWidgetDropdown(index: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.openWidgetDropdown === index) {
      this.openWidgetDropdown = null;
    } else {
      this.openWidgetDropdown = index;
    }
  }

  public onWidgetDropdownClick(action: string, widget: WidgetItem, index: number): void {
    console.log(`Widget dropdown action: ${action}`, widget);
    
    // Close the dropdown
    this.openWidgetDropdown = null;
    
    // Handle different actions
    switch (action) {
      case 'edit':
        // Add edit functionality here
        console.log('Edit widget:', widget);
        break;
      case 'settings':
        // Add settings functionality here
        console.log('Settings for widget:', widget);
        break;
      case 'remove':
        // Add remove functionality here
        console.log('Remove widget:', widget);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  public toggleSectionDropdown(sectionId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.openSectionDropdown === sectionId) {
      this.openSectionDropdown = null;
    } else {
      this.openSectionDropdown = sectionId;
    }
  }

  public onSectionDropdownClick(action: string, sectionId: string): void {
    console.log(`Section dropdown action: ${action} for section: ${sectionId}`);
    
    // Close the dropdown
    this.openSectionDropdown = null;
    
    // Handle different actions
    switch (action) {
      case 'edit':
        // Add edit functionality here
        console.log('Edit section:', sectionId);
        break;
      case 'settings':
        // Add settings functionality here
        console.log('Settings for section:', sectionId);
        break;
      case 'hide':
        // Add hide functionality here
        console.log('Hide section:', sectionId);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  // Close radial menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isRadialMenuOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.radial_menu_btn') && !target.closest('.radial_menu')) {
        this.isRadialMenuOpen = false;
      }
    }
    
    // Close widget dropdown when clicking outside
    if (this.openWidgetDropdown !== null) {
      const target = event.target as HTMLElement;
      if (!target.closest('.widget_dropdown_container')) {
        this.openWidgetDropdown = null;
      }
    }
    
    // Close section dropdown when clicking outside
    if (this.openSectionDropdown !== null) {
      const target = event.target as HTMLElement;
      if (!target.closest('.section_dropdown_container')) {
        this.openSectionDropdown = null;
      }
    }
  }

  // Keyboard navigation support
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isRadialMenuOpen) {
      switch (event.key) {
        case 'Escape':
          this.isRadialMenuOpen = false;
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const index = parseInt(event.key) - 1;
          if (this.radialMenuItems[index]) {
            this.onRadialMenuItemClick(this.radialMenuItems[index], event);
          }
          break;
      }
    }
  }

  hasChildren(item: NavigationItem): boolean {
    return !!item.subs && item.subs.length > 0;
  }

  /**
   * Get the href for a sub-item to enable right-click context menu
   * This generates a proper href that the browser can use for "Open in new tab" functionality
   * Uses relative path with hash for Angular hash location strategy
   */
  getSubItemHref(subItem: NavigationSubItem): string {
    // For right-click context menu, we need a valid href
    // Use relative path with hash to avoid full page navigation
    const hash = '#'; // App uses hash location strategy
    
    // Special handling for "Projects I Own"
    if (subItem.to === '/projects/projects-i-own') {
      return `${hash}/projects/projects-all?scope=my-project`;
    }
    
    // For detail pages, try to get the current ID from the URL
    const currentUrl = this.router.url;
    
    // Handle project detail pages
    if (subItem.objCode === 'PROJECT') {
      const projectMatch = currentUrl.match(/\/projects\/project-details\/([^\/]+)/);
      if (projectMatch) {
        const projectId = projectMatch[1];
        const subRoutePath = subItem.to.split('/').pop();
        if (subRoutePath && subRoutePath !== 'project-details') {
          return `${hash}/projects/project-details/${projectId}/${subRoutePath}`;
        }
        return `${hash}/projects/project-details/${projectId}`;
      }
    }
    
    // Handle task/revision/issue/request detail pages
    const detailPageTypes = ['task', 'revision', 'issue', 'request'];
    for (const detailType of detailPageTypes) {
      if (currentUrl.includes(`/${detailType}/${detailType}-details/`)) {
        const detailMatch = currentUrl.match(new RegExp(`\\/${detailType}\\/${detailType}-details\\/([^\\/]+)`));
        if (detailMatch && subItem.to.startsWith(`/${detailType}/`)) {
          const detailId = detailMatch[1];
          const subRoutePath = subItem.to.split('/').pop();
          if (subRoutePath && subRoutePath !== `${detailType}-details`) {
            return `${hash}/${detailType}/${detailType}-details/${detailId}/${subRoutePath}`;
          }
          return `${hash}/${detailType}/${detailType}-details/${detailId}`;
        }
      }
    }
    
    // Default: use the subItem.to route
    return `${hash}${subItem.to}`;
  }

  /**
   * Handle context menu (right-click) - allow browser default behavior
   */
  onSubItemContextMenu(event: MouseEvent): void {
    // Allow default browser context menu to appear
    // Don't prevent default - this enables "Open in new tab" option
    return;
  }

  onSubItemClick(subItem: NavigationSubItem, event: MouseEvent): void {
    // Right-click (button 2): Allow default browser behavior to show context menu
    // Middle-click (button 1): Allow default to open in new tab
    // Ctrl/Cmd+click: Allow default to open in new tab
    // Also check event.which for older browser compatibility
    const isRightClick = event.button === 2 || (event as any).which === 3;
    const isMiddleClick = event.button === 1 || (event as any).which === 2;
    const isModifierClick = event.ctrlKey || event.metaKey || event.shiftKey;
    
    if (isRightClick || isMiddleClick || isModifierClick) {
      // Don't prevent default - let browser handle context menu and new tab opening
      // The href attribute will allow "Open in new tab" option in context menu
      return;
    }
    
    // Left-click (button 0): Prevent default and use Angular router
    // This ensures SPA navigation in SAME tab without page reload
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Ensure we're handling a left-click (button 0 or undefined)
    if (event.button !== 0 && event.button !== undefined) {
      return;
    }
    
    // console.log('=== SIDEBAR - Sub Item Click ===');
    // console.log('Clicked sub item:', subItem);

    this.navigationService.setActiveSubItem(subItem);

    // Special handling for "Project Documents" - now functional
    if (subItem.objCode === 'PROJECT' && subItem.label === 'Documents') {
      // Navigate to Documents page
      const currentUrl = this.router.url;
      const projectMatch = currentUrl.match(/\/projects\/project-details\/([^\/]+)/);

      if (projectMatch) {
        const projectId = projectMatch[1];
        this.router.navigate(['/projects/project-details', projectId, 'documents']);
      } else {
        // Fallback for safety
        this.router.navigate([subItem.to]);
      }
      return;
    }

    // Special handling for "Projects I Own" menu item
    if (subItem.to === '/projects/projects-i-own') {
      // Override navigation to redirect to projects-all with scope parameter
      this.router.navigate(['/projects/projects-all'], {
        queryParams: { scope: 'my-project' }
      });
      return;
    }

    // Special handling for "All Projects" menu item
    if (subItem.to === '/projects/projects-all') {
      // Navigate to clean projects-all URL without any query parameters
      // This will trigger the query parameter handler to clear scope filters
      this.router.navigate(['/projects/projects-all']);
      console.log('🔍 All Projects menu clicked - navigating to clean URL');
      return;
    }

    if (subItem.objCode === 'PROJECT') {
      // Get current project ID from route
      const currentUrl = this.router.url;
      const projectMatch = currentUrl.match(/\/projects\/project-details\/([^\/]+)/);

      if (projectMatch) {
        const projectId = projectMatch[1];
        const subRoutePath = subItem.to.split('/').pop();

        if (subRoutePath) {
          this.router.navigate(['/projects/project-details', projectId, subRoutePath]);
        } else {
          // Fallback for safety
          this.router.navigate([subItem.to]);
        }
      } else {
        // If no project ID found, use default navigation
        this.router.navigate([subItem.to]);
      }
    } else {
      // Check for specific detail page types first
      const currentUrl = this.router.url;
      const detailPageTypes = ['task', 'revision', 'issue', 'request'];
      let detailNavigation = null;
      
      for (const detailType of detailPageTypes) {
        if (currentUrl.includes(`/${detailType}/${detailType}-details/`)) {
          // Extract the ID from the current URL
          const detailMatch = currentUrl.match(new RegExp(`\\/${detailType}\\/${detailType}-details\\/([^\\/]+)`));
          if (detailMatch) {
            const detailId = detailMatch[1];
            const subRoutePath = subItem.to.split('/').pop();
            
            // Check if the sub-item belongs to this detail page type
            if (subItem.to.startsWith(`/${detailType}/`)) {
              if (subRoutePath) {
                detailNavigation = [`/${detailType}/${detailType}-details`, detailId, subRoutePath];
              } else {
                detailNavigation = [`/${detailType}/${detailType}-details`, detailId];
              }
              break;
            }
          }
        }
      }
      
      // If no specific detail page match found, try dynamic navigation
      if (!detailNavigation) {
        detailNavigation = this.handleDetailPageNavigation(subItem);
      }
      
      if (detailNavigation) {
        this.router.navigate(detailNavigation, { replaceUrl: false });
      } else {
        // If no detail page match found, use default navigation
        this.router.navigate([subItem.to]);
      }
    }
  }

  isSubItemActive(subItem: NavigationSubItem): boolean {
    const currentUrl = this.router.url;
    const fullUrl = window.location.href; // Get full URL including query params

    // Special handling for "Projects I Own" menu item - OVERRIDE navigation service logic
    if (subItem.to === '/projects/projects-i-own' || 
        subItem.label?.toLowerCase().includes('projects i own') ||
        subItem.label?.toLowerCase().includes('my projects') ||
        subItem.label?.toLowerCase().includes('i own')) {
      // Check if we're on projects-all with scope=my-project
      if (currentUrl.includes('/projects/projects-all')) {
        // Handle hash routing - query params come after the hash
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const scope = urlParams.get('scope');
        const isActive = scope === 'my-project';
        return isActive;
      }
      return false;
    }

    // Special handling for "All Projects" menu item - OVERRIDE navigation service logic
    if (subItem.to === '/projects/projects-all') {
      // Check if we're on projects-all without scope parameter
      if (currentUrl.includes('/projects/projects-all')) {
        // Handle hash routing - query params come after the hash
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const scope = urlParams.get('scope');
        const isActive = !scope || scope !== 'my-project';
        return isActive;
      }
      return false;
    }

    // For all other items, use the navigation service logic
    const isActive = this.navigationService.isSubItemActive(subItem);

    // Additional check for current route
    if (!isActive) {
      // Check if current URL matches the sub-item route
      if (currentUrl === subItem.to) {
        return true;
      }

      // Check for project/task detail routes
      if (subItem.objCode === 'PROJECT' && currentUrl.includes('/projects/project-details/')) {
        const subRoutePath = subItem.to.split('/').pop();
        // Remove hash fragment if present for comparison
        const urlWithoutHash = currentUrl.split('#')[0];
        const currentSubRoute = urlWithoutHash.split('/').pop();
        return subRoutePath === currentSubRoute;
      }

      // Check for specific detail page types
      const detailPageTypes = ['task', 'revision', 'issue', 'request'];
      for (const detailType of detailPageTypes) {
        if (currentUrl.includes(`/${detailType}/${detailType}-details/`)) {
          // Check if the sub-item belongs to this detail page type
          if (subItem.to.startsWith(`/${detailType}/`)) {
            const subRoutePath = subItem.to.split('/').pop();
            // Remove hash fragment if present for comparison
            const urlWithoutHash = currentUrl.split('#')[0];
            const currentSubRoute = urlWithoutHash.split('/').pop();
            
            // Handle special cases like 'edit'
            if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
              return true;
            }
            
            // Handle detail page main route
            if (subRoutePath === undefined && !currentSubRoute) {
              return true;
            }
            
            return subRoutePath === currentSubRoute;
          }
        }
      }

      // Handle detail pages dynamically (fallback)
      const detailPageMatch = this.handleDetailPageActiveCheck(subItem, currentUrl);
      if (detailPageMatch !== null) {
        return detailPageMatch;
      }
    }

    return isActive;
  }

  isHeaderActive(header: NavigationItem): boolean {
    const isActive = this.navigationService.isHeaderActive(header);

    // Additional check for dashboard routes
    if (header.to === '/dashboard' || header.to === '/') {
      return this.isMainPage;
    }

    return isActive;
  }

  shouldShowSidebar(): boolean {
    // Show sidebar if we have an active header with subs, or if we have navigation items
    const hasActiveHeader = this.activeHeader !== null && !this.activeHeader.hideSidebar;
    const hasNavigationItems = this.navigationItems && this.navigationItems.length > 0;
    
    // Also show sidebar if we're on a detail page (even without active header)
    const isOnDetailPage = this.getCurrentDetailPageInfo() !== null;
    
   
    
    return hasActiveHeader || hasNavigationItems || isOnDetailPage;
  }

  private openSubmenus = new Set<string>();

  toggleSubmenu(navItem: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.openSubmenus.has(navItem.to)) {
      this.openSubmenus.delete(navItem.to);
    } else {
      this.openSubmenus.add(navItem.to);
    }
  }

  isSubmenuOpen(navItem: NavigationItem): boolean {
    return this.openSubmenus.has(navItem.to);
  }

  getCurrentPageSubmenu(): NavigationItem | null {
    // Use the NavigationService's active header which is already set based on current route
    if (this.activeHeader) {
      return this.activeHeader;
    }
    
    // If no active header, check if we're on a detail page and try to find the appropriate navigation item
    const detailPageInfo = this.getCurrentDetailPageInfo();
    if (detailPageInfo) {
      // Look for navigation items that match the detail page type
      for (const item of this.navigationItems) {
        if (item.subs && item.subs.some(sub => sub.to.startsWith(`/${detailPageInfo.type}/`))) {
          return item;
        }
      }
    }
    
    return null;
  }

  getSubmenuCount(subItem: NavigationSubItem): number {
    return this.submenuCountService.getCountFor(subItem);
  }

  getSubmenuHours(subItem: NavigationSubItem): string {
    return this.submenuCountService.getHoursFor(subItem);
  }

  getSubmenuIcon(subItem: NavigationSubItem): string {
    // Use subItem.icon when on /people or /reports page
    const currentUrl = this.router.url;
    if (subItem.icon && (currentUrl.startsWith('/people') || currentUrl.startsWith('/reports'))) {
      return subItem.icon;
    }

    // Map submenu items to existing SVG icons
    const iconMap: { [key: string]: string } = {
      // Project related icons
      'all-projects': 'wl_icon_all_project',
      'projects-i-own': 'wl_icon_project_i_own',
      'project-details': 'wl_icon_prtdetail',
      'add-project': 'wl_icon_approvals',
      'edit-project': 'wl_icon_revisions',

      // Client related icons
      'clients': 'assets/images/people.svg',
      'client-details': 'assets/images/person.png',
      'add-client': 'assets/images/apply.svg',

      // Dashboard related icons
      'dashboard': 'assets/images/dt1.svg',
      'overview': 'assets/images/dt2.svg',
      'analytics': 'assets/images/dt3.svg',
      'reports': 'assets/images/dt4.svg',

      // Task related icons
      'tasks': 'wl_icon_task',
      'my-tasks': 'wl_icon_mytask',
      'task-details': 'wl_icon_task_details',
      'add-task': 'wl_icon_approvals',

      // Team related icons
      'team': 'assets/images/left_sidebar/my_team.svg',
      'teams': 'wl_icon_team',
      'my-team': 'assets/images/left_sidebar/my_team.svg',
      'team-members': 'assets/images/people.svg',
      
      // People related icons
      'people': 'wl_icon_people',

      // Approval related icons
      'approvals': 'wl_icon_approvals',
      'my-approvals': 'assets/images/left_sidebar/my_approvals.svg',
      'pending-approvals': 'assets/images/left_sidebar/pending.svg',

      // Time related icons
      'timesheet': 'assets/images/left_sidebar/my_timesheet.svg',
      'my-timesheet': 'assets/images/left_sidebar/my_timesheet.svg',
      'time-log': 'assets/images/calender.svg',
      'hours': 'wl_icon_log-time',

      // Issue related icons
      'issues': 'wl_icon_issues',
      'documents': 'wl_icon_documents',
      'staffing': 'wl_icon_staffing',
      'updates': 'wl_icon_updates',
      'qa-issues': '',
      'bugs': '',

      // Queue related icons
      'queues': 'assets/images/left_sidebar/queues.svg',
      'my-queues': 'assets/images/left_sidebar/queues.svg',

      // Request related icons
      'requests': 'wl_icon_request',
      'my-requests': 'wl_icon_request',
      'revisions': 'wl_icon_revisions',

      // Leave related icons
      'leave': 'assets/images/left_sidebar/apply_leave.svg',
      'apply-leave': 'assets/images/left_sidebar/apply_leave.svg',
      'whos-off': 'assets/images/left_sidebar/whos_off.svg',

      // Assignment related icons
      'assignments': 'assets/images/left_sidebar/daily_assignments.svg',
      'daily-assignments': 'assets/images/left_sidebar/daily_assignments.svg',

      // Settings related icons
      'settings': 'assets/images/settings.svg',
      'profile': 'assets/images/user.png',
      'account': 'assets/images/user.png',

      // Default icons
      'default': 'wl_icon_template',
      'list': 'assets/images/left_sidebar/my_tasks.svg',
      'view': 'assets/images/left_sidebar/my_approvals.svg',
      'create': 'assets/images/apply.svg',
      'edit': 'assets/images/edit.svg',
      'delete': 'assets/images/close.svg'
    };

     // Try to match by label (case insensitive) - add null check
     if (subItem.label) {
      const label = subItem.label.toLowerCase().replace(/\s+/g, '-');
      if (iconMap[label]) {
        return iconMap[label];
      }
    }

    // Try to match by route path
    if (subItem.to) {
      const route = subItem.to.toLowerCase().replace(/\//g, '-').replace(/^-/, '');
      if (iconMap[route]) {
        return iconMap[route];
      }
    }

    if (subItem.objCode) {
      if (iconMap[subItem.objCode.toLowerCase()]) {
        return iconMap[subItem.objCode.toLowerCase()];
      }
    }

    // Return default icon if no match found
    return iconMap['default'] || 'wl_icon_template';
  }

  private forceRefreshMenuState(): void {
    // Force refresh the menu state by triggering change detection
    this.cdr.detectChanges();
    
    // Also force the navigation service to update
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  setUserInfoBg(bg: string) {
    this.userInfoBg = bg;
    this.cdr.detectChanges();
  }

  refreshUserInfoBackground() {
    // Force refresh the user info background
    this.setGreetingAndBg();
    this.cdr.detectChanges();
  }

  forceRefreshTimeBasedBackground() {
    // Force refresh to current time-based background (ignoring theme state temporarily)
    const hour = new Date().getHours();
    let period = 'morning';

    if (hour >= 5 && hour < 12) {
      this.greeting = 'Good Morning';
      period = 'morning';
      this.needsTextOverlay = true;
    } else if (hour >= 12 && hour < 17) {
      this.greeting = 'Good Afternoon';
      period = 'afternoon';
      this.needsTextOverlay = true;
    } else if (hour >= 17 && hour < 20) {
      this.greeting = 'Good Evening';
      period = 'evening';
      this.needsTextOverlay = false;
    } else {
      this.greeting = 'Good Night';
      period = 'night';
      this.needsTextOverlay = false;
    }

    this.currentTimePeriod = period as 'morning' | 'afternoon' | 'evening' | 'night';

    // Always set time-based background when this method is called
    const bgImages: { [key: string]: string[] } = {
      morning: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_morning_1.jpg',
        'assets/images/left_sidebar/greeting_bg/greet_bg_morning_2.jpg',
      ],
      afternoon: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_1.jpg',
      ],
      evening: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_evening_1.png',
      ],
      night: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_night_1.jpg',
        'assets/images/left_sidebar/greeting_bg/greet_bg_night_2.jpg',
      ],
    };

    const imgs = bgImages[period] || bgImages['morning'];
    this.userInfoBg = imgs[Math.floor(Math.random() * imgs.length)];

    // Force change detection multiple times to ensure proper rendering
    this.cdr.detectChanges();

    // Additional delayed detection to ensure UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 10);
  }

  /**
   * Handles detail page navigation dynamically based on current URL and sub-item
   * 
   * This method extracts patterns from the actual navigation items to make the system
   * truly data-driven. It looks for items that have detail-like patterns in their subs.
   * 
   * @param subItem - The sub-item being clicked
   * @returns Navigation array or null if not a detail page
   */
  private handleDetailPageNavigation(subItem: NavigationSubItem): string[] | null {
    const currentUrl = this.router.url;
    
    // Handle specific detail page types that we know about
    const detailPagePatterns = [
      { pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/, basePath: 'task/task-details' },
      { pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/, basePath: 'revision/revision-details' },
      { pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/, basePath: 'issue/issue-details' },
      { pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/, basePath: 'request/request-details' }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        const itemId = match[1];
        const subRoutePath = subItem.to.split('/').pop();

        // Build the base path
        const basePath = `/${detailPage.basePath}/${itemId}`;

        // Handle special cases like 'edit'
        if (subRoutePath === 'edit') {
          return [basePath, 'edit'];
        } else if (subRoutePath) {
          return [basePath, subRoutePath];
        } else {
          // For detail page main route
          return [basePath];
        }
      }
    }

    // Fallback to the old dynamic pattern extraction
    const navigationItems = this.navigationItems;
    
    // Look for items that might be detail pages based on their structure
    for (const item of navigationItems) {
      // Skip project details as they're handled separately
      if (item.name === 'ProjectDetail') {
        continue;
      }

      // Check if this item has subs and if any of them suggest a detail page pattern
      if (item.subs && item.subs.length > 0) {
        // Try to extract a pattern from the first sub-item
        const firstSub = item.subs[0];
        const pattern = this.extractDetailPattern(firstSub.to, item.name);
        
        if (pattern) {
          const match = currentUrl.match(pattern);
          if (match) {
            const itemId = match[1];
            const subRoutePath = subItem.to.split('/').pop();

            // Build the base path dynamically
            const basePath = this.buildDetailPath(pattern, itemId);

            // Handle special cases like 'edit'
            if (subRoutePath === 'edit') {
              return [basePath, 'edit'];
            } else if (subRoutePath) {
              return [basePath, subRoutePath];
            } else {
              // For detail page main route
              return [basePath];
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Extracts a detail pattern from a sub-item's URL
   * @param subUrl - The URL of a sub-item (e.g., "/task/updates")
   * @param itemName - The name of the parent item
   * @returns Regex pattern for matching detail URLs, or null if not a detail page
   */
  private extractDetailPattern(subUrl: string, itemName: string): RegExp | null {
    // Extract the base path from the sub URL
    // e.g., "/task/updates" -> "/task"
    const urlParts = subUrl.split('/').filter(part => part);
    if (urlParts.length < 2) return null;

    const basePath = `/${urlParts[0]}`;
    const entityName = urlParts[0];
    
    // Check if this looks like a detail page based on item name
    if (itemName.toLowerCase().includes('detail')) {
      // Create pattern for detail pages: /entity/entity-details/id/subroute
      return new RegExp(`\\/${entityName}\\/${entityName}-details\\/([^\\/]+)(?:\\/(.+))?`);
    }

    return null;
  }

  /**
   * Builds the main detail path
   * @param pattern - The regex pattern used to match the URL
   * @param itemId - The ID of the detail item
   * @returns The constructed detail path
   */
  private buildDetailPath(pattern: RegExp, itemId: string): string {
    // Extract the base path from the pattern
    const patternStr = pattern.source;
    const basePathMatch = patternStr.match(/\\\/([^\\\/]+)\\\/[^\\\/]+-details/);
    if (basePathMatch) {
      const entityName = basePathMatch[1];
      return `/${entityName}/${entityName}-details/${itemId}`;
    }
    return '';
  }

  /**
   * Checks if we're currently on a detail page and returns the detail page info
   * @returns Object with detail page info or null if not on a detail page
   */
  private getCurrentDetailPageInfo(): { type: string; id: string; subRoute?: string } | null {
    const currentUrl = this.router.url;
    
    const detailPagePatterns = [
      { type: 'task', pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'revision', pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'issue', pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'request', pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/ }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        return {
          type: detailPage.type,
          id: match[1],
          subRoute: match[2]
        };
      }
    }

    return null;
  }

  /**
   * Handles detail page active state checking dynamically
   * @param subItem - The sub-item to check
   * @param currentUrl - The current URL
   * @returns Boolean indicating if the sub-item is active, or null if not a detail page
   */
  private handleDetailPageActiveCheck(subItem: NavigationSubItem, currentUrl: string): boolean | null {
    // Handle specific detail page types that we know about
    const detailPagePatterns = [
      { pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/, basePath: 'task/task-details' },
      { pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/, basePath: 'revision/revision-details' },
      { pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/, basePath: 'issue/issue-details' },
      { pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/, basePath: 'request/request-details' }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        const subRoutePath = subItem.to.split('/').pop();
        const currentSubRoute = currentUrl.split('/').pop();

        // Handle special cases like 'edit'
        if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
          return true;
        }

        // Handle detail page main route
        if (subRoutePath === undefined && !currentSubRoute) {
          return true;
        }

        return subRoutePath === currentSubRoute;
      }
    }

    // Fallback to the old dynamic pattern extraction
    const navigationItems = this.navigationItems;
    
    // Look for items that might be detail pages based on their structure
    for (const item of navigationItems) {
      // Skip project details as they're handled separately
      if (item.name === 'ProjectDetail') {
        continue;
      }

      // Check if this item has subs and if any of them suggest a detail page pattern
      if (item.subs && item.subs.length > 0) {
        // Try to extract a pattern from the first sub-item
        const firstSub = item.subs[0];
        const pattern = this.extractDetailPattern(firstSub.to, item.name);
        
        if (pattern) {
          const match = currentUrl.match(pattern);
          if (match) {
            const subRoutePath = subItem.to.split('/').pop();
            const currentSubRoute = currentUrl.split('/').pop();

            // Handle special cases like 'edit'
            if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
              return true;
            }

            // Handle detail page main route
            if (subRoutePath === undefined && !currentSubRoute) {
              return true;
            }

            return subRoutePath === currentSubRoute;
          }
        }
      }
    }

    return null;
  }

  getWorkingCount() {
    this.navigationService.getWorkingCount().subscribe({
      next: (response) => {
        // Ensure response.data is a valid array
        const dataArr = Array.isArray(response?.data) ? response.data : [];
        this.statusItems = this.statusItems.map(item => {
          if (item.label === 'Working on') {
            const workingItem = dataArr.find((d: any) => d._id === 'working');
            return { ...item, count: workingItem?.count || 0 };
          } else if (item.label === 'Pending') {
            const requestedItem = dataArr.find((d: any) => d._id === 'requested');
            return { ...item, count: requestedItem?.count || 0 };
          } else if (item.label === 'Approvals') {
            const requestedItem = dataArr.find((d: any) => d._id === 'approval-request');
            return { ...item, count: requestedItem?.count || 0 };
          }
          return item;
        });
      },
      error: (error) => {
        // Silently set all counts to 0 on error
        this.statusItems = this.statusItems.map(item => ({ ...item, count: 0 }));
      }
    });
  }

  /**
   * Check if we're on a task or revision detail page and load time summary
   */
  private checkAndLoadTimeSummary(): void {
    const currentUrl = this.router.url;
    const taskMatch = currentUrl.match(/\/task\/task-details\/([^\/]+)/);
    const revisionMatch = currentUrl.match(/\/revision\/revision-details\/([^\/]+)/);
    
    if (taskMatch && taskMatch[1]) {
      const itemId = taskMatch[1];
      this.currentItemId.set(itemId);
      this.currentItemType.set('task');
      this.showTimeSummary.set(true);
      this.loadItemTimeSummary(itemId, 'TASK');
    } else if (revisionMatch && revisionMatch[1]) {
      const itemId = revisionMatch[1];
      this.currentItemId.set(itemId);
      this.currentItemType.set('revision');
      this.showTimeSummary.set(true);
      this.loadItemTimeSummary(itemId, 'REVISION');
    } else {
      this.showTimeSummary.set(false);
      this.currentItemId.set(null);
      this.currentItemType.set(null);
      this.taskHourStatus.set(null);
    }
  }

  /**
   * Load time summary data for a task or revision
   */
  private loadItemTimeSummary(itemId: string, objCode: 'TASK' | 'REVISION'): void {
    this.isTimeSummaryLoading.set(true);
    
    // First, check if we have cached data from projectHoursService
    const cachedData = this.projectHoursService.getFullHourData(itemId, objCode);
    
    if (cachedData) {
      const dataHash = this.createDataHash(cachedData);
      this.lastProcessedDataHash = dataHash;
      this.processHourSummaryData(cachedData);
      return;
    }
    
    // If no cached data, make API call
    this.logTimeService.getHourData(itemId, objCode).subscribe({
      next: (response) => {
        if (response && response.data) {
          const dataHash = this.createDataHash(response.data);
          this.lastProcessedDataHash = dataHash;
          this.processHourSummaryData(response.data);
        } else {
          this.isTimeSummaryLoading.set(false);
        }
      },
      error: (error) => {
        console.error(`Error loading ${objCode.toLowerCase()} time summary:`, error);
        this.isTimeSummaryLoading.set(false);
      }
    });
  }

  /**
   * Process hour summary data and update component state
   */
  private processHourSummaryData(data: any, skipLoadingState: boolean = false): void {
    const transformedData = {
      totalPlannedHours: data.plannedHour || 0,
      hourFilledData: data.onsetHours || 0,
      unfilledHours: (data.plannedHour || 0) - (data.onsetHours || 0),
      offsetHours: data.offsetHours || 0,
      totalBillableHours: data.onsetHours || 0,
      totalTaskHours: data.onsetHours || 0,
      totalProjectHours: 0,
      totalRequestHours: 0,
      totalRevisionHours: 0,
      totalNonBillableHours: 0,
      totalIssueHours: 0,
      totalOffsetHours: data.offsetHours || 0
    };
    
    // Check if data has actually changed to avoid unnecessary chart re-initialization
    const currentData = this.taskHourData();
    const hasChanged = !currentData || 
      currentData.hourFilledData !== transformedData.hourFilledData ||
      currentData.totalPlannedHours !== transformedData.totalPlannedHours ||
      currentData.offsetHours !== transformedData.offsetHours;
    
    this.taskHourData.set(transformedData);
    this.taskHourStatus.set(transformedData);
    
    // Get percentageComplete from ProjectHoursService (set by status-sidebar from API response)
    const itemId = this.currentItemId();
    const itemType = this.currentItemType();
    if (itemId && itemType) {
      const objCode = itemType.toUpperCase() as 'TASK' | 'REVISION';
      const percentageComplete = this.projectHoursService.getPercentageComplete(itemId, objCode);
      
      if (percentageComplete !== null && percentageComplete !== undefined) {
        // Use percentageComplete from API response (from status-sidebar)
        this.taskPercentage.set(Math.round(percentageComplete));
      } else {
        // Fallback: Calculate task percentage if percentageComplete not available
        const plannedHours = data.plannedHour || 0;
        const filledHours = data.onsetHours || 0;
        
        if (plannedHours > 0) {
          const percentage = Math.min((filledHours / plannedHours) * 100, 100);
          this.taskPercentage.set(Math.round(percentage));
        } else {
          this.taskPercentage.set(0);
        }
      }
    } else {
      // Fallback: Calculate task percentage if item info not available
      const plannedHours = data.plannedHour || 0;
      const filledHours = data.onsetHours || 0;
      
      if (plannedHours > 0) {
        const percentage = Math.min((filledHours / plannedHours) * 100, 100);
        this.taskPercentage.set(Math.round(percentage));
      } else {
        this.taskPercentage.set(0);
      }
    }
    
    if (!skipLoadingState) {
      this.isTimeSummaryLoading.set(false);
    }
    
    // Only re-initialize charts if data has changed or charts don't exist
    if (hasChanged || this.chartInstances.length === 0) {
      // Initialize charts after data is loaded with proper delay
      setTimeout(() => {
        this.initializeCharts();
      }, 200);
    }
  }

  /**
   * Check for cached hour data updates and refresh if available
   */
  private checkAndUpdateFromCache(): void {
    if (this.isProcessingCacheUpdate) {
      return;
    }

    const itemId = this.currentItemId();
    const itemType = this.currentItemType();
    
    if (!itemId || !itemType) {
      return;
    }

    const cachedHours = this.projectHoursService.getCurrentProjectHours()();
    
    // Check if cached data exists and matches current item
    if (!cachedHours || !cachedHours.fullHourData) {
      return;
    }

    // Check if cached data matches current task or revision
    const isMatch = 
      (itemType === 'task' && cachedHours.taskID === itemId) ||
      (itemType === 'revision' && cachedHours.revisionID === itemId);
    
    if (!isMatch) {
      return;
    }

    // Create a hash of the data to check if it has changed
    const dataHash = this.createDataHash(cachedHours.fullHourData);
    
    // Only process if data has actually changed
    if (dataHash === this.lastProcessedDataHash) {
      return;
    }

    // Set flag to prevent concurrent processing
    this.isProcessingCacheUpdate = true;
    this.lastProcessedDataHash = dataHash;

    try {
      // Refresh the time summary data with cached data
      // Skip loading state since this is a reactive update from cache
      this.processHourSummaryData(cachedHours.fullHourData, true);
    } finally {
      // Reset flag after a short delay to allow processing to complete
      setTimeout(() => {
        this.isProcessingCacheUpdate = false;
      }, 100);
    }
  }

  /**
   * Create a hash of the hour data to detect changes
   */
  private createDataHash(data: any): string {
    if (!data) return '';
    return `${data.plannedHour || 0}_${data.onsetHours || 0}_${data.offsetHours || 0}`;
  }

  /**
   * Format hours for display
   */
  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }

    const absValue = Math.abs(value);
    const integerPart = Math.floor(absValue).toString();

    if (integerPart.length >= 4) {
      const truncatedInteger = integerPart.substring(0, 4);
      return (value < 0 ? '-' : '') + truncatedInteger;
    }

    const remainingDigits = Math.min(4 - integerPart.length, 2);

    if (remainingDigits <= 0) {
      return (value < 0 ? '-' : '') + integerPart;
    }

    const multiplier = Math.pow(10, remainingDigits);
    const roundedValue = Math.round(absValue * multiplier) / multiplier;

    let result = roundedValue.toFixed(remainingDigits);

    if (result.includes('.')) {
      result = result.replace(/\.?0+$/, '');
    }

    return (value < 0 ? '-' : '') + result;
  }

  ngAfterViewInit(): void {
    // Initialize charts if data is already loaded
    if (this.showTimeSummary() && this.taskHourStatus()) {
      setTimeout(() => {
        this.initializeCharts();
      }, 300);
    }

    // Watch for changes in cached hour data and refresh time summary if visible
    // Use interval to check for cache updates (polling approach to avoid infinite loops)
    // Check every 1 second to balance responsiveness with performance
    interval(1000).pipe(
      takeUntil(this.destroy$),
      filter(() => {
        // Only check when time summary is visible
        const shouldCheck = this.showTimeSummary() && 
                           this.currentItemId() !== null && 
                           !this.isProcessingCacheUpdate;
        return shouldCheck === true;
      })
    ).subscribe(() => {
      this.checkAndUpdateFromCache();
    });
  }

  /**
   * Initialize charts
   */
  private async initializeCharts(): Promise<void> {
    if (!this.taskHourStatus() || !this.taskHourData()) {
      this.isChartsLoading.set(false);
      return;
    }

    try {
      this.destroyCharts();
      this.isChartsLoading.set(true);

      // Add timeout to hide spinner if charts take too long
      this.loadingTimeout = setTimeout(() => {
        if (this.isChartsLoading()) {
          this.isChartsLoading.set(false);
        }
      }, 5000);

      // Wait for DOM to be ready
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 100));

      // Ensure containers are available
      if (!this.taskStatusChartContainer?.nativeElement || !this.balanceHoursChartContainer?.nativeElement) {
        console.warn('Chart containers not available');
        this.isChartsLoading.set(false);
        return;
      }

      // Initialize task status chart
      this.initTaskStatusChart();
      // Initialize balance hours chart
      this.initBalanceHoursChart();

      this.isChartsLoading.set(false);
      this.clearTimeouts();
    } catch (error) {
      console.error('Error initializing charts:', error);
      this.isChartsLoading.set(false);
    }
  }

  /**
   * Initialize task status chart
   */
  private initTaskStatusChart(): void {
    if (!this.taskStatusChartContainer?.nativeElement) return;

    const percentage = this.taskPercentage();
    if (percentage === undefined || percentage === null) return;

    try {
      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 130,
          width: 130,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: -150,
          endAngle: 150,
          background: [{
            outerRadius: '100%',
            innerRadius: '88%',
            backgroundColor: '#A7BBCE',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        series: [{
          type: 'solidgauge',
          name: 'Completion',
          data: [{
            y: percentage,
            color: '#F46D73',
            radius: '100%',
            innerRadius: '88%'
          }]
        }],
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: false,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              className: 'task-status-chart-label',
              formatter: function (this: any) {
                return `${this.y}%`;
              },
              style: {
                fontSize: '26px',
                fontWeight: '600',
                fontFamily: 'Outfit, sans-serif',
              }
            }
          }
        }
      };

      const chart = Highcharts.chart(this.taskStatusChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating task status chart:', error);
    }
  }

  /**
   * Initialize balance hours chart
   */
  private initBalanceHoursChart(): void {
    if (!this.balanceHoursChartContainer?.nativeElement) return;

    const hourStatus = this.taskHourStatus();
    if (!hourStatus || hourStatus.unfilledHours === undefined) return;

    try {
      const percent = Math.abs(hourStatus.unfilledHours) / (hourStatus.totalPlannedHours || 1) * 100;
      const redArcLength = Math.min(percent, 100);

      const chartOptions: Highcharts.Options = {
        chart: {
          type: 'solidgauge',
          backgroundColor: 'transparent',
          height: 130,
          width: 130,
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0]
        },
        title: { text: undefined },
        credits: { enabled: false },
        tooltip: { enabled: false },
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [{
            outerRadius: '100%',
            innerRadius: '75%',
            backgroundColor: '#7cc74d',
            shape: 'arc',
            borderWidth: 0
          }]
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
          labels: { enabled: false }
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: true,
              borderWidth: 0,
              useHTML: true,
              y: 0,
              verticalAlign: 'middle',
              align: 'center',
              className: 'balance-hours-chart-label',
              formatter: function (this: any) {
                const unfilledHours = hourStatus.unfilledHours || 0;
                return `<div style="text-align: center; font-family: 'Outfit', sans-serif;">
                          <div class="balance-main-text" style="font-size: 12px; font-weight: 500; line-height: 1.1;">Balance<br>Hours</div>
                          <div class="balance-sub-text" style="font-size: 13px; font-weight: 600;">${unfilledHours.toFixed(2)}</div>
                        </div>`;
              }
            },
            linecap: 'round',
            rounded: true,
            stickyTracking: false
          }
        },
        series: [{
          type: 'solidgauge',
          name: 'Balance',
          data: [{
            y: redArcLength,
            color: '#f26666',
            radius: '85%',
            innerRadius: '68%'
          }]
        }]
      };

      const chart = Highcharts.chart(this.balanceHoursChartContainer.nativeElement, chartOptions);
      this.chartInstances.push(chart);
    } catch (error) {
      console.error('Error creating balance hours chart:', error);
    }
  }

  /**
   * Force chart initialization (used when percentageComplete changes)
   */
  private forceChartInitialization(): void {
    this.destroyCharts();
    this.isChartsLoading.set(true);
    setTimeout(() => {
      if (this.showTimeSummary() && this.taskHourStatus()) {
        this.initializeCharts();
      }
    }, 200);
  }

  /**
   * Destroy charts
   */
  private destroyCharts(): void {
    this.clearTimeouts();

    this.chartInstances.forEach(chart => {
      try {
        if (chart && chart.destroy && typeof chart.destroy === 'function') {
          chart.destroy();
        }
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    });
    this.chartInstances = [];

    if (this.taskStatusChartContainer?.nativeElement) {
      this.taskStatusChartContainer.nativeElement.innerHTML = '';
    }
    if (this.balanceHoursChartContainer?.nativeElement) {
      this.balanceHoursChartContainer.nativeElement.innerHTML = '';
    }
  }

  /**
   * Clear timeouts
   */
  private clearTimeouts(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }


}
